import { useCallback, useEffect, useState } from "react";
import { Check, EyeOff, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { call, getJson } from "@/lib/api";
import { postJson } from "@/lib/auth";

const KIND_LABEL = { topic: "Tema", reply: "Respuesta", post: "Pared", comment: "Comentario en noticia" };

const Row = ({ kind, d, onAction }) => (
  <div className="flex flex-wrap items-start gap-4 border-b border-white/10 py-4" data-testid={`mod-row-${d.id}`}>
    <div className="min-w-0 flex-1">
      <p className="mono text-[11px] uppercase tracking-[0.14em] text-[var(--muted)]">
        {KIND_LABEL[kind]} · {d.author?.name} · {new Date(d.created_at).toLocaleString("es-MX")}
        {d.status === "pending" && <span className="ml-2 rounded-full bg-[var(--green)] px-2 py-0.5 text-[#0d0c0a]">{d.auto_hidden ? "Oculto por reportes" : "Pendiente"}</span>}
        {d.reports?.length > 0 && <span className="ml-2 rounded-full bg-[#ff8a7a] px-2 py-0.5 text-[#0d0c0a]" data-testid={`mod-reports-${d.id}`}>{d.reports.length} reporte{d.reports.length === 1 ? "" : "s"}</span>}
      </p>
      {d.reports?.length > 0 && <p className="mt-1 text-xs text-[#ffb3a7]">Motivos: {d.reports.map((r) => r.reason + (r.note ? ` (“${r.note}”)` : "")).join(", ")}</p>}
      {d.title && <p className="font-display mt-1 text-xl">{d.title}</p>}
      <p className="mt-1 line-clamp-3 text-sm">{d.body}</p>
    </div>
    {d.image_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${d.image_url}`} alt="" className="h-16 w-16 rounded-lg object-cover" />}
    <div className="flex gap-2">
      {d.status === "pending" ? (
        <button type="button" onClick={() => onAction(kind, d, "published")} className="btn-primary !px-3 !py-2" data-testid={`mod-approve-${d.id}`}><Check size={14} /> Aprobar</button>
      ) : (
        <button type="button" onClick={() => onAction(kind, d, "pending")} className="btn-ghost !px-3 !py-2" title="Ocultar" data-testid={`mod-hide-${d.id}`}><EyeOff size={14} /></button>
      )}
      <button type="button" onClick={() => onAction(kind, d, "delete")} className="btn-ghost !px-3 !py-2" title="Borrar" data-testid={`mod-delete-${d.id}`}><Trash2 size={14} /></button>
    </div>
  </div>
);

export const AdminCommunity = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => getJson("/admin/community").then(setData), []);
  useEffect(() => {
    load();
  }, [load]);
  const toggle = async (v) => {
    const r = await postJson("/admin/community/settings", { require_approval: v }, "PUT");
    setData((d) => ({ ...d, require_approval: r.require_approval }));
    toast.success(v ? "Las nuevas publicaciones esperarán tu aprobación" : "Las publicaciones salen al instante");
  };
  const action = async (kind, d, what) => {
    if (what === "delete" && !window.confirm("¿Borrar esta publicación para siempre?")) return;
    if (what === "delete") await call(`/admin/community/${kind}/${d.id}`, { method: "DELETE" });
    else await postJson(`/admin/community/${kind}/${d.id}`, { status: what }, "PATCH");
    load();
  };
  if (!data) return <Loader2 className="mx-auto mt-10 animate-spin" />;
  const rows = ["topic", "reply", "post", "comment"].flatMap((k) => (data[k] ?? []).map((d) => ({ k, d })))
    .sort((a, b) => (b.d.status === "pending" || b.d.reports?.length > 0) - (a.d.status === "pending" || a.d.reports?.length > 0) || b.d.created_at.localeCompare(a.d.created_at));
  return (
    <div data-testid="admin-community">
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-white/10 bg-[var(--card)] p-5">
        <div>
          <p className="font-display text-2xl">Moderación</p>
          <p className="mt-1 text-sm text-[var(--muted)]">{data.require_approval ? "Cada publicación espera tu aprobación antes de verse." : "Las publicaciones salen al instante; tú borras lo que no quieras."}</p>
        </div>
        <label className="flex items-center gap-3 text-sm">
          Requerir aprobación
          <Switch checked={data.require_approval} onCheckedChange={toggle} data-testid="mod-require-approval-switch" />
        </label>
      </div>
      <p className="mono mt-6 text-[11px] text-[var(--muted)]" data-testid="mod-count">{rows.length} publicaciones · {rows.filter((r) => r.d.status === "pending").length} pendientes</p>
      <div className="mt-2">{rows.map(({ k, d }) => <Row key={d.id} kind={k} d={d} onAction={action} />)}</div>
    </div>
  );
};
